// Kept free of `next/headers` and `next/navigation` so the middleware can import it on the edge.
export const LOCALES = ["de", "en", "immo"] as const;

export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "de";

export const isLocale = (value: string | null | undefined): value is Locale =>
  !!value && (LOCALES as readonly string[]).includes(value);

// Strapi keeps the immo site, a German variant, under a locale code of its own.
const STRAPI_IMMO_LOCALE = "de-LI";

export const toStrapiLocale = (locale: Locale) =>
  locale === "immo" ? STRAPI_IMMO_LOCALE : locale;

export const fromStrapiLocale = (
  value: string | null | undefined,
): Locale | null =>
  value === STRAPI_IMMO_LOCALE ? "immo" : isLocale(value) ? value : null;

const LOCALE_PREFIX = new RegExp(`^/(${LOCALES.join("|")})(?=[/?#]|$)`);

export const splitLocale = (
  pathname: string,
): { locale: Locale; path: string; prefixed: boolean } => {
  const match = pathname.match(LOCALE_PREFIX);

  if (!match) {
    return { locale: DEFAULT_LOCALE, path: pathname, prefixed: false };
  }

  return {
    locale: match[1] as Locale,
    path: pathname.slice(match[0].length) || "/",
    prefixed: true,
  };
};

export const stripLocale = (pathname: string) => splitLocale(pathname).path;

const isInternalPage = (href: string) =>
  href.startsWith("/") &&
  !href.startsWith("//") &&
  !/^\/(api|_next)(\/|$)/.test(href);

/**
 * Prefixes a root-relative href with the locale it should stay in. Next did this on its own
 * while `i18n` was set in next.config.js, so path builders all over the codebase still return
 * German, unprefixed paths; external URLs, anchors and paths that already name a locale pass
 * through untouched.
 */
export const localizePath = (locale: Locale, href: string): string => {
  if (
    locale === DEFAULT_LOCALE ||
    !isInternalPage(href) ||
    splitLocale(href).prefixed
  ) {
    return href;
  }

  return href === "/" || href.startsWith("/?") || href.startsWith("/#")
    ? `/${locale}${href.slice(1)}`
    : `/${locale}${href}`;
};

/**
 * The tag `Intl` will accept. "immo" is a German-language variant of the site, not a
 * language: `new Intl.PluralRules("immo")` throws RangeError, and every plural, date and
 * number would go with it.
 */
export const BCP47: Record<Locale, string> = {
  de: "de",
  en: "en",
  immo: "de",
};

export const interpolate = (
  message: string,
  values: Record<string, string | number>,
): string =>
  message.replace(/\{\{(\w+)\}\}/g, (whole, name: string) =>
    name in values ? String(values[name]) : whole,
  );
